import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, MapPin } from "lucide-react";
import { api, useApi } from "../api";
import { PageShell } from "../components/Layout";
import { MaskLines, Reveal, Marquee, ChapterTag, SpotlightCard, FaqAccordion } from "../components/Motion";
import Seo, { faqSchema } from "../components/Seo";
import CoverageChecker from "../components/CoverageChecker";
import { Loading } from "../components/Sections";

const HERO_IMG = "https://images.unsplash.com/photo-1601670753941-6360a3f9008c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Home() {
  const { data, loading } = useApi(api.home, []);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 160]);
  const glowY = useTransform(scrollY, [0, 700], [0, -60]);

  if (loading || !data) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Seo title={data.title} description={data.description} path="/" schema={faqSchema(data.faqs)} />

      <section className="relative min-h-[78vh] flex items-end overflow-hidden tactical-grid" data-testid="home-hero">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -top-24">
          <img src={HERO_IMG} alt="London skyline at dusk" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink" />
        </motion.div>
        <motion.div
          style={{ y: glowY }}
          className="absolute -right-32 top-16 font-display font-black text-[38vw] leading-none text-outline select-none pointer-events-none hidden lg:block"
          aria-hidden="true"
        >
          X
        </motion.div>
        <div className="relative w-full max-w-7xl mx-auto px-5 sm:px-8 pb-14 sm:pb-20 pt-24">
          <Reveal y={12}>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-amber" /> UK Security Operations // SIA Licensed
            </p>
          </Reveal>
          <MaskLines
            lines={["ELITE SECURITY", "GUARD SERVICES", "ACROSS THE UK"]}
            className="font-display font-black uppercase leading-[0.92] tracking-tight text-[15vw] sm:text-7xl lg:text-8xl text-white"
          />
          <Reveal delay={0.55}>
            <p className="mt-7 max-w-2xl text-base sm:text-lg text-gray-300 leading-relaxed">
              Xtreme Protection deploys SIA-licensed security guards, mobile patrols, CCTV monitoring and rapid
              response teams across <span className="text-amber font-semibold">210+ UK towns and cities</span> —
              engineered deterrence with verified proof on every shift.
            </p>
          </Reveal>
          <Reveal delay={0.7}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/locations"
                data-testid="hero-explore-locations-btn"
                className="group flex items-center gap-3 bg-amber text-ink font-display font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-white transition-colors duration-300"
              >
                Explore 210+ UK Locations
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-view-services-btn"
                className="group flex items-center gap-3 border border-white/25 text-white font-display font-bold uppercase tracking-widest text-sm px-7 py-4 hover:border-amber hover:text-amber transition-colors duration-300"
              >
                View All Security Services
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CoverageChecker />

      <section className="border-b border-white/10 bg-coal/40" data-testid="home-stats">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4">
          {data.stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className={`py-8 px-4 sm:px-8 ${i > 0 ? "border-l border-white/10" : ""}`}>
              <div className="font-display font-black text-3xl sm:text-5xl text-amber">{s.value}</div>
              <div className="mt-2 font-mono text-[10px] sm:text-xs uppercase tracking-widest text-gray-400">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={data.marquee} />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="chapter-standard">
        <ChapterTag num={data.chapters[0].num} title={data.chapters[0].title} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
              {data.chapters[0].heading}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="space-y-5">
            {data.chapters[0].body.map((p, i) => (
              <p key={i} className="text-sm sm:text-base text-gray-400 leading-relaxed">{p}</p>
            ))}
            <div className="flex flex-wrap gap-2 pt-2">
              {["SIA Licensed", "BS 7858 Vetted", "ISO 9001", "£10m Insured"].map((b) => (
                <span key={b} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-amber border border-amber/30 px-2.5 py-1.5">
                  <ShieldCheck className="w-3 h-3" /> {b}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-24" data-testid="chapter-arsenal">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterTag num={data.chapters[1].num} title={data.chapters[1].title} />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-3xl">
              {data.chapters[1].heading}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-2xl text-sm sm:text-base text-gray-400 leading-relaxed">{data.chapters[1].body[0]}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="home-services-grid">
            {data.services.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 4) * 0.06}>
                <Link to={`/services/${s.slug}`} data-testid={`home-service-card-${s.slug}`} className="block h-full">
                  <SpotlightCard className="h-full p-6 hover:border-amber/60 transition-colors duration-300">
                    <div className="font-mono text-[10px] text-amber tracking-widest">{String(i + 1).padStart(2, "0")}</div>
                    <h3 className="mt-3 font-display font-bold uppercase tracking-wide text-xl text-white leading-tight">{s.name}</h3>
                    <p className="mt-3 text-xs sm:text-sm text-gray-400 leading-relaxed">{s.short}</p>
                    <div className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber">
                      Intel <ArrowRight className="w-3 h-3" />
                    </div>
                  </SpotlightCard>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 sm:py-24" data-testid="chapter-territory">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterTag num={data.chapters[2].num} title={data.chapters[2].title} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <Reveal>
              <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
                {data.chapters[2].heading}
              </h2>
              <p className="mt-5 max-w-xl text-sm sm:text-base text-gray-400 leading-relaxed">{data.chapters[2].body[0]}</p>
              <Link
                to="/locations"
                data-testid="territory-all-locations-btn"
                className="group mt-8 inline-flex items-center gap-3 border border-amber text-amber font-display font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-amber hover:text-ink transition-colors duration-300"
              >
                Full UK Directory
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="featured-cities-grid">
              {data.featured_cities.map((c, i) => (
                <Reveal key={c.slug} delay={(i % 2) * 0.08}>
                  <Link
                    to={`/locations/${c.slug}`}
                    data-testid={`featured-city-${c.slug}`}
                    className="group flex items-center justify-between gap-3 border border-white/10 bg-card px-5 py-4 hover:border-amber transition-colors duration-300"
                  >
                    <span>
                      <span className="block font-display font-bold uppercase tracking-wide text-lg text-white group-hover:text-amber transition-colors">
                        Security {c.name}
                      </span>
                      <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">{c.county}</span>
                    </span>
                    <MapPin className="w-4 h-4 text-amber shrink-0" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3" data-testid="region-counts">
            {data.regions.map((r) => (
              <span key={r.region} className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                <span className="text-amber">{r.count}</span> — {r.region}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-24" data-testid="chapter-answers">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterTag num="04" title="THE BRIEFING" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight mb-10">
              Security questions, answered
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <FaqAccordion faqs={data.faqs} testIdPrefix="home-faq" />
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
